/* eslint-disable camelcase */
/* eslint-disable max-lines-per-function */
import React from "react";

import { Form, Formik, useField } from "formik";
import { Button, CircularProgress, Grid } from "@material-ui/core";
import { useMutation } from "@apollo/client";
import * as yup from "yup";
import { SelectAutocomplete } from "components/haven-design/form/select";

import { useSnackbar } from "components/haven-design/snackbar";

import { AdminListUsersQuery_adminApplicants, AdminMergeAccountsMutation, AdminMergeAccountsMutationVariables } from "types/gql-types";
import { DialogActions, DialogContent } from "components/haven-design/modal/dialog";
import { MergeAccountsMutation } from "./queries";


const mergeAccountSchema = yup.object({
  firstName: yup.string(),
  lastName: yup.string(),
  loginEmail: yup.string().nullable(),
  loginPhone: yup.string().nullable(),
});

type Values = yup.InferType<typeof mergeAccountSchema>
const initialValues: Values = {
  firstName: "",
  lastName: "",
  loginEmail: "",
  loginPhone: "",
};

type TypeaheadTextInputProps = { name: string, label: string, options: string[] }
const TypeaheadTextInput = ({ name, label, options }: TypeaheadTextInputProps) => {
  const [{ value },, { setValue }] = useField<string>(name);

  return (
    <SelectAutocomplete
      freeSolo
      label={label}
      options={options}
      value={value}
      onChange={(_, newValue) => newValue && setValue(newValue)}
      onInputChange={(_, newValue) => setValue(newValue)} />
  );
};

type Props = {
  users: AdminListUsersQuery_adminApplicants[]
  close: () => void
}

const MergeAccounts: React.FC<Props> = ({ users, close }) => {
  const { openSnackbar } = useSnackbar();
  const [mergeAccounts, { loading }] = useMutation<
  AdminMergeAccountsMutation,
  AdminMergeAccountsMutationVariables
  >(MergeAccountsMutation);

  const handleMergeAccounts = async (variables: Values) => {
    // eslint-disable-next-line no-alert
    if (!confirm("Are you sure you want to merge these accounts?")) return;

    try {
      const ids = users.map(({ id }) => id);
      await mergeAccounts({ variables: { ids, ...variables } });

      openSnackbar({ message: "Merged Accounts Successfully", status: "success" });
      close();
    } catch (e) {
      openSnackbar({ message: "Merging Accounts Failed", status: "error" });
    }
  };

  const firstNames = users.map(({ profile }) => (profile?.firstName)).filter(Boolean) as string[];
  const lastNames = users.map(({ profile }) => (profile?.lastName)).filter(Boolean) as string[];
  const loginEmails = users.map(({ loginEmail }) => (loginEmail)).filter(Boolean) as string[];
  const loginPhones = users.map(({ loginPhone }) => (loginPhone)).filter(Boolean) as string[];

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={mergeAccountSchema}
      onSubmit={handleMergeAccounts}>
      <>
        <DialogContent>
          <Form id="merge-accounts">
            <Grid container>
              <Grid item xs={12}>
                <p>
              Merging {users.length} user accounts into one.
              Please pick the best name and phone for the final account.
              When merged, all interests will be merged into a single account.
                </p>
              </Grid>

              {loading && (
                <Grid container item justifyContent="center">
                  <CircularProgress />
                  <p>Merging Accounts ...</p>
                </Grid>
              )}

              {!loading && (
                <>
                  <Grid item xs={12}>
                    <TypeaheadTextInput name="firstName" label="First Name" options={firstNames} />
                  </Grid>

                  <Grid item xs={12}>
                    <TypeaheadTextInput name="lastName" label="Last Name" options={lastNames} />
                  </Grid>

                  <Grid item xs={12}>
                    <TypeaheadTextInput name="loginEmail" label="Login Email" options={loginEmails} />
                  </Grid>

                  <Grid item xs={12}>
                    <TypeaheadTextInput name="loginPhone" label="Login Phone" options={loginPhones} />
                  </Grid>

                </>
              )}
            </Grid>
          </Form>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" type="button" color="default" onClick={close}>Cancel</Button>
          <Button variant="contained" type="submit" color="secondary" form="merge-accounts">Merge</Button>
        </DialogActions>
      </>
    </Formik>
  );
};

export default MergeAccounts;
